import { FC, useState } from 'react';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Drawer,
  Dropdown,
  Flex,
  MenuProps,
  Space
} from 'antd';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/stores';
import { LinkIcon } from '@/components';
import { language as global, menu as menuIcon, logo } from '@/assets';
import { articlePath, homePath } from '@/utils/articlePath';
import styles from './Header.module.css';

interface IProps {
  isFixed: boolean;
}

interface OperationBarProps {
  isDrawer?: boolean;
}

const HeaderView: FC<IProps> = observer(function Header({ isFixed = true }) {
  const { t } = useTranslation();
  const { locale, facility } = useStore();
  const navigator = useNavigate();
  const location = useLocation();
  const { site_name } = useParams();
  const [langItems] = useState<MenuProps['items']>([
    // 暂时隐藏日语切换
    // {
    //   key: 'ja',
    //   label: t('lang.ja')
    // },
    {
      key: 'en',
      label: t('lang.en')
    },
    {
      key: 'zh',
      label: t('lang.zh')
    },
    {
      key: 'zh-Hant',
      label: t('lang.zh-Hant')
    },
    {
      key: 'ko',
      label: t('lang.ko')
    }
  ]);
  const [showDrawer, setShowDrawer] = useState(false);
  const isHomePage = ['/', '/index', '/:site_name', '/:site_name/index'].some(
    path => !!matchPath({ path, end: true }, location.pathname)
  );

  const goHome = () => {
    navigator(
      homePath(site_name) +
        '?lang=' +
        (locale.lang === 'zh-Hant' ? 'tw' : locale.lang),
      { replace: true }
    );
  };

  const onClick: MenuProps['onClick'] = ({ key }) => {
    locale.setLang(key);
    if (isHomePage) {
      const searchParams = new URLSearchParams(
        window.location.hash.split('?')[1]
      );
      searchParams.set('lang', key === 'zh-Hant' ? 'tw' : key);
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
    window.location.reload();
  };

  const HeaderLogoElement = () => {
    return (
      <Flex className={styles.headerLogoCon} justify="center" align="center">
        <LinkIcon
          iconUrl={logo}
          className={styles.logo}
          iconHeight={56}
          onClick={goHome}
        />
      </Flex>
    );
  };

  const OperationBar = (props: OperationBarProps) => {
    const { isDrawer = false } = props;

    return (
      <Space
        size={24}
        direction={isDrawer ? 'vertical' : 'horizontal'}
        className={!isDrawer ? styles.mobilebtnGroup : ''}
      >
        {!isDrawer ? (
          <Dropdown
            menu={{
              items: langItems,
              defaultSelectedKeys: [locale.lang],
              selectable: true,
              onClick
            }}
            trigger={['click']}
            placement="bottom"
            overlayClassName={styles.dropdownContainer}
          >
            <Flex align="center" gap={8}>
              <LinkIcon iconUrl={global} className={styles.langIcon} />
              <span className={styles.language}>
                {(
                  langItems?.find(v => v?.key === locale.lang) as {
                    label: string;
                  }
                )?.label || ''}
              </span>
            </Flex>
          </Dropdown>
        ) : (
          <Flex vertical justify="center" align="center">
            <LinkIcon iconUrl={global} className={styles.globle} />
            {!!langItems &&
              langItems.map(v => {
                return (
                  <Button
                    type={locale.lang === v?.key ? 'link' : 'text'}
                    block
                    key={v?.key}
                    className={
                      locale.lang === v?.key ? styles.registration : styles.btn
                    }
                    onClick={() => {
                      if (locale.lang === v?.key) {
                        setShowDrawer(false);
                        return;
                      }
                      locale.setLang(v?.key as string);
                      if (isHomePage) {
                        const searchParams = new URLSearchParams(
                          window.location.hash.split('?')[1]
                        );
                        searchParams.set(
                          'lang',
                          (v?.key as string) === 'zh-Hant'
                            ? 'tw'
                            : (v?.key as string)
                        );
                        const newUrl = `${
                          window.location.pathname
                        }?${searchParams.toString()}`;
                        window.history.replaceState({}, '', newUrl);
                      }
                      window.location.reload();
                    }}
                  >
                    {(v as { label: string }).label}
                  </Button>
                );
              })}
          </Flex>
        )}
      </Space>
    );
  };

  const onHandleDrawerDisplay = (field: boolean) => {
    setShowDrawer(field);
  };

  const navigateToTickets = () => {
    navigator(
      articlePath(
        facility.getTicketsSlug() || 'tickets',
        site_name,
        facility.getTicketsDocumentId()
      )
    );
  };

  const titleList = [
    {
      title: t('header.areaGuide'),
      id: 'area-guide',
      visible: facility.showAreaGuideNav
    },
    {
      title: t('header.modelCourses'),
      id: 'model-courses',
      visible: facility.showModelCoursesNav
    },
    {
      title: t('header.travelGuide'),
      id: 'travel-guide',
      visible: facility.showTravelGuideNav
    }
  ].filter(item => item.visible);

  return (
    <div
      className={styles.header}
      style={{ position: isFixed ? 'sticky' : 'relative' }}
    >
      <div className={styles.nav}>
        <HeaderLogoElement />
        <Flex gap={40} align="center" className={styles.mobilebtnGroup}>
          {isHomePage && (
            <>
              {titleList.map(item => {
                return (
                  <span
                    key={item.id}
                    className={styles.titleItem}
                    onClick={() => {
                      window.location.href = `#${item.id}`;
                    }}
                  >
                    {item.title}
                  </span>
                );
              })}
              {facility.showTicketsNav && (
                <span
                  key={'tickets'}
                  className={styles.titleItem}
                  onClick={() => {
                    navigateToTickets();
                  }}
                >
                  {t('header.tickets')}
                </span>
              )}
            </>
          )}
        </Flex>
        <div></div>
        <section className={styles.btnGroup}>
          <OperationBar />
          <LinkIcon
            iconUrl={menuIcon}
            className={`${styles.globle} ${styles.mobileMenu}`}
            onClick={() => onHandleDrawerDisplay(true)}
          />
        </section>
      </div>
      <Drawer
        title={<HeaderLogoElement />}
        placement="left"
        closable={false}
        onClose={() => onHandleDrawerDisplay(false)}
        open={showDrawer}
        width={'85%'}
        classNames={{ content: styles.drawerBg }}
      >
        <Flex
          vertical
          justify="center"
          align="center"
          style={{ width: '100%' }}
        >
          <OperationBar isDrawer={true} />
          {isHomePage && (
            <Flex vertical justify="center" align="center">
              <Divider />
              {titleList.map(item => {
                return (
                  <span
                    key={item.id}
                    className={styles.titleItem}
                    onClick={() => {
                      onHandleDrawerDisplay(false);
                      window.location.href = `#${item.id}`;
                    }}
                  >
                    {item.title}
                  </span>
                );
              })}
              {facility.showTicketsNav && (
                <span
                  key={'tickets'}
                  className={styles.titleItem}
                  onClick={() => {
                    onHandleDrawerDisplay(false);
                    navigateToTickets();
                  }}
                >
                  {t('header.tickets')}
                </span>
              )}
            </Flex>
          )}
        </Flex>
      </Drawer>
    </div>
  );
});

export default HeaderView;
